import { useCallback, useEffect, useRef, useState } from "react";
import posthog from "posthog-js";
import { api } from "../../services/api";
import { useUser } from "../../services/user-context";
import {
  fetchSurveyConfig,
  sendSurveyDismissed,
  sendSurveyShown,
  sendSurveySent,
} from "./survey-service";
import { SurveyConfig, SurveyResponses } from "./survey-types";

// Toggle to skip backend survey tracking (validate status + register completion)
const SKIP_BACKEND_SURVEY_TRACKING = false;

export function useSurvey(sessionId: string) {
  const { userInfo } = useUser() as { userInfo: { email: string } };
  const [surveyConfig, setSurveyConfig] = useState<SurveyConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldShowSurvey, setShouldShowSurvey] = useState(false);
  const surveyStartTime = useRef<Date | null>(null);
  const hasSubmitted = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setIsLoading(false);
      return;
    }

    async function initSurvey() {
      // Identify user so PostHog links events to a person (matches iOS behavior)
      if (userInfo?.email) {
        posthog.identify(userInfo.email);
      }

      try {
        if (!SKIP_BACKEND_SURVEY_TRACKING) {
          // Validate if survey was already completed for this session
          const statusResponse = await api.validateSurveyStatus(sessionId);
          const { survey_id, session_id } = statusResponse.data || {};

          if (survey_id && session_id) {
            setIsLoading(false);
            return;
          }
        }

        const config = await fetchSurveyConfig();
        if (config) {
          setSurveyConfig(config);
          setShouldShowSurvey(true);
        }
      } catch (error) {
        // On error, still show survey (fail-safe, same as iOS)
        const config = await fetchSurveyConfig();
        if (config) {
          setSurveyConfig(config);
          setShouldShowSurvey(true);
        }
      } finally {
        setIsLoading(false);
      }
    }

    initSurvey();
  }, [sessionId, userInfo?.email]);

  const trackSurveyShown = useCallback(() => {
    if (!surveyConfig) return;
    surveyStartTime.current = new Date();
    sendSurveyShown(surveyConfig, sessionId);
  }, [surveyConfig, sessionId]);

  const trackSurveySent = useCallback(
    async (thumbType: "up" | "down", selectedChips: string[]) => {
      if (!surveyConfig) return;

      const timeSpentSeconds = surveyStartTime.current
        ? Math.round((new Date().getTime() - surveyStartTime.current.getTime()) / 1000)
        : 0;

      // Build responses with PostHog question IDs
      const responses: SurveyResponses = {
        [`$survey_response_${surveyConfig.ratingQuestionId}`]: thumbType === "up" ? "Good" : "Bad",
      };

      if (thumbType === "up") {
        responses[`$survey_response_${surveyConfig.positiveOptionsQuestionId}`] = selectedChips;
      } else {
        responses[`$survey_response_${surveyConfig.negativeOptionsQuestionId}`] = selectedChips;
      }

      sendSurveySent(surveyConfig, sessionId, timeSpentSeconds, responses);
      hasSubmitted.current = true;

      if (!SKIP_BACKEND_SURVEY_TRACKING) {
        try {
          await api.registerSurveyCompletion({
            sessionID: sessionId,
            surveyID: surveyConfig.surveyId,
          });
        } catch (error) {
          console.error("[Survey] Failed to register survey completion:", error);
        }
      }
    },
    [surveyConfig, sessionId]
  );

  const trackSurveyDismissed = useCallback(() => {
    if (!surveyConfig || hasSubmitted.current) return;

    const timeSpentSeconds = surveyStartTime.current
      ? Math.round((new Date().getTime() - surveyStartTime.current.getTime()) / 1000)
      : 0;

    sendSurveyDismissed(surveyConfig, sessionId, timeSpentSeconds);
  }, [surveyConfig, sessionId]);

  return {
    surveyConfig,
    isLoading,
    shouldShowSurvey,
    trackSurveyShown,
    trackSurveySent,
    trackSurveyDismissed,
    setShouldShowSurvey,
  };
}
